'use client'

import {
  useEffect,
  useState,
} from 'react'
import {
  Loader2,
} from 'lucide-react'
import {
  customIntegrationsService,
  getUserFacingApiErrorMessage,
  promotedIntegrationsService,
  type PromotedIntegrationSummary,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'

// Shared by the first-party ``IntegrationTile`` (in IntegrationCategoriesSection)
// and the promoted ``DynamicIntegrationTile`` below. Lives here rather than in
// IntegrationCategoriesSection to avoid a circular import — that module already
// imports from this one.
export function DisconnectModal({
  name,
  onConfirm,
  onClose,
}: {
  name: string
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Disconnect {name}?</h3>
        <p className="text-sm text-gray-500 mb-5">This will remove the connection. You can reconnect at any time.</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => { trackEvent(OnboardingEvents.INTEGRATION_DISCONNECT_CANCELLED, { name }); onClose() }}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { onConfirm(); onClose() }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  )
}

// Promoted (monty-backed) integrations are registered at build time on the
// backend and exposed via ``GET /integrations/promoted``. Rather than living
// in a standalone section, each promoted integration is rendered inside the
// matching category card of ``IntegrationCategoriesSection`` (e.g. Fireflies
// next to Granola/Fathom/Otter under "Note Taker"). The building blocks below
// are shared by that section.

// Mirrors ``PromotedCategory`` in the backend
// (app/agents/toolsets/integrations/base.py). Keep in sync.
export const PROMOTED_CATEGORY = {
  MESSAGING: 'messaging',
  VIDEO_CONFERENCING: 'video_conferencing',
  NOTE_TAKER: 'note_taker',
  PROJECT_MANAGEMENT: 'project_management',
  KNOWLEDGE_BASE: 'knowledge_base',
  CRM: 'crm',
  FINANCE: 'finance',
  SCHEDULING: 'scheduling',
  OTHER: 'other',
} as const

const CATEGORY_LABEL: Record<string, string> = {
  messaging: 'Messaging',
  video_conferencing: 'Video Conferencing',
  note_taker: 'Note Taker',
  project_management: 'Project Management',
  knowledge_base: 'Knowledge Base',
  crm: 'CRM',
  finance: 'Finance',
  scheduling: 'Scheduling',
  other: 'Other',
}

const MONOGRAM_PALETTE = [
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-rose-500',
  'bg-amber-500',
  'bg-violet-500',
  'bg-sky-500',
  'bg-teal-500',
]

function monogramColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0
  }
  return MONOGRAM_PALETTE[hash % MONOGRAM_PALETTE.length] ?? 'bg-gray-500'
}

export function DynamicIntegrationTile({
  integration,
  loading,
  onConnect,
  onDisconnect,
}: {
  integration: PromotedIntegrationSummary
  loading: boolean
  onConnect: () => void
  onDisconnect: () => void
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const [showDisconnect, setShowDisconnect] = useState(false)
  const showImg = integration.logo_url && !imgFailed

  // Connected tiles are clickable too (they open the disconnect modal) — the
  // first-party IntegrationTile behaves the same. Only a pending request blocks.
  const handleClick = () => {
    if (loading) return
    if (integration.connected) setShowDisconnect(true)
    else onConnect()
  }

  return (
    <>
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`w-full flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-colors relative group ${
        integration.connected
          ? 'bg-green-50 border-2 border-green-200 hover:bg-green-100'
          : 'bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm cursor-pointer'
      } ${loading ? 'opacity-60 cursor-wait' : ''}`}
    >
      {loading ? (
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      ) : showImg && integration.logo_url ? (
        <img
          src={integration.logo_url}
          alt={integration.display_name}
          className="w-8 h-8 object-contain rounded"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div
          className={`w-8 h-8 rounded ${monogramColor(integration.name)} flex items-center justify-center text-white text-sm font-semibold`}
        >
          {integration.display_name.charAt(0).toUpperCase()}
        </div>
      )}
      <span className="text-xs font-medium text-gray-700 text-center">
        {integration.display_name}
      </span>
      {integration.connected && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <polyline points="20 6 9 17 4 12" strokeWidth={3} />
          </svg>
        </div>
      )}
    </button>
    {showDisconnect && (
      <DisconnectModal
        name={integration.display_name}
        onConfirm={onDisconnect}
        onClose={() => setShowDisconnect(false)}
      />
    )}
    </>
  )
}

// Fallback card for promoted integrations whose category has no hardcoded home
// in ``IntegrationCategoriesSection`` (e.g. ``other``). Ensures nothing is ever
// silently dropped from the UI.
export function PromotedCategoryCard({
  category,
  integrations,
  pendingName,
  onConnect,
  onDisconnect,
}: {
  category: string
  integrations: PromotedIntegrationSummary[]
  pendingName: string | null
  onConnect: (integration: PromotedIntegrationSummary) => void
  onDisconnect: (integration: PromotedIntegrationSummary) => void
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 text-lg font-semibold">
          {(CATEGORY_LABEL[category] || category).charAt(0)}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">
            {CATEGORY_LABEL[category] || category}
          </h3>
          <p className="text-xs text-gray-500">Promoted integrations</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {integrations.map((it) => (
          <DynamicIntegrationTile
            key={it.name}
            integration={it}
            loading={pendingName === it.name}
            onConnect={() => onConnect(it)}
            onDisconnect={() => onDisconnect(it)}
          />
        ))}
      </div>
    </div>
  )
}

export interface UsePromotedIntegrations {
  /** Promoted integrations grouped by their backend category string. */
  byCategory: Map<string, PromotedIntegrationSummary[]>
  /** Name of the integration whose connect/disconnect flow is currently running. */
  pendingName: string | null
  error: string | null
  connect: (integration: PromotedIntegrationSummary) => Promise<void>
  disconnect: (integration: PromotedIntegrationSummary) => Promise<void>
}

/**
 * Loads promoted integrations and exposes the per-user connect flow. Consumed
 * by ``IntegrationCategoriesSection`` to render each promoted integration in
 * its matching category card.
 */
export function usePromotedIntegrations(): UsePromotedIntegrations {
  const [items, setItems] = useState<PromotedIntegrationSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [pendingName, setPendingName] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await promotedIntegrationsService.list()
        if (!cancelled) setItems(data)
      } catch (err) {
        if (!cancelled) {
          setError(getUserFacingApiErrorMessage(err, "Couldn't load these integrations just now."))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const connect = async (integration: PromotedIntegrationSummary) => {
    trackEvent(OnboardingEvents.PROMOTED_INTEGRATION_CONNECT_CLICKED, {
      integration: integration.name,
      auth_kind: integration.auth_kind,
    })
    setPendingName(integration.name)
    setError(null)
    try {
      const { spec_id } = await promotedIntegrationsService.ensureSpec(
        integration.name,
      )
      if (integration.auth_kind === 'oauth_ac') {
        window.location.href = customIntegrationsService.oauthUrl(spec_id)
        return
      }
      // API_KEY / BASIC_AUTH → magic-link credential entry
      const url = await customIntegrationsService.createSecretRequest(spec_id)
      window.location.href = url
    } catch (err) {
      setError(
        getUserFacingApiErrorMessage(
          err,
          `Couldn't start connecting ${integration.display_name} just now. Give it another try.`,
        ),
      )
      setPendingName(null)
    }
  }

  const disconnect = async (integration: PromotedIntegrationSummary) => {
    trackEvent(OnboardingEvents.PROMOTED_INTEGRATION_DISCONNECT_CLICKED, {
      integration: integration.name,
      auth_kind: integration.auth_kind,
    })
    setPendingName(integration.name)
    setError(null)
    try {
      await promotedIntegrationsService.disconnect(integration.name)
      // Flip the tile back to connectable in place (no full refetch needed).
      setItems((prev) =>
        prev.map((it) =>
          it.name === integration.name ? { ...it, connected: false } : it,
        ),
      )
    } catch (err) {
      setError(
        getUserFacingApiErrorMessage(
          err,
          `Couldn't disconnect ${integration.display_name} just now. Give it another try.`,
        ),
      )
    } finally {
      setPendingName(null)
    }
  }

  const byCategory = new Map<string, PromotedIntegrationSummary[]>()
  for (const it of items) {
    const list = byCategory.get(it.category) ?? []
    list.push(it)
    byCategory.set(it.category, list)
  }

  return { byCategory, pendingName, error, connect, disconnect }
}
